/**
 * @brisa/ui · PlaceholderState — componente compartilhado dos 3 placeholders (I5 USM)
 *
 * Variants (app / hub / admin) controlam escala, stagger delays e transform do Squiggle.
 */
import * as React from 'react';
import { Squiggle } from './squiggle';

export type PlaceholderVariant = 'app' | 'hub' | 'admin';

export interface PlaceholderStateProps {
  variant: PlaceholderVariant;
  eyebrow: string;
  lead: string;
  body: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
}

const VARIANT_CONFIG: Record<
  PlaceholderVariant,
  {
    squiggle: { rotation: number; scale: number; skew: number };
    leadClass: string;
    bodyClass: string;
    wrapperClass: string;
  }
> = {
  app: {
    squiggle: { rotation: 0, scale: 1, skew: 0 },
    leadClass: 'brisa-placeholder-lead brisa-placeholder-lead--app',
    bodyClass: 'brisa-placeholder-body brisa-placeholder-body--app',
    wrapperClass: 'brisa-placeholder brisa-placeholder--app',
  },
  hub: {
    squiggle: { rotation: 45, scale: 1.2, skew: 0 },
    leadClass: 'brisa-placeholder-lead brisa-placeholder-lead--hub',
    bodyClass: 'brisa-placeholder-body brisa-placeholder-body--hub',
    wrapperClass: 'brisa-placeholder brisa-placeholder--hub',
  },
  admin: {
    squiggle: { rotation: -15, scale: 0.9, skew: 5 },
    leadClass: 'brisa-placeholder-lead brisa-placeholder-lead--admin',
    bodyClass: 'brisa-placeholder-body brisa-placeholder-body--admin',
    wrapperClass: 'brisa-placeholder brisa-placeholder--admin',
  },
};

export function PlaceholderState({
  variant,
  eyebrow,
  lead,
  body,
  children,
  footer,
}: PlaceholderStateProps) {
  const cfg = VARIANT_CONFIG[variant];

  return (
    <main className={cfg.wrapperClass}>
      <div className="brisa-placeholder-inner">
        <p className="brisa-placeholder-eyebrow" data-stagger="1">
          {eyebrow}
        </p>
        <h1 className={`${cfg.leadClass} brisa-display`} data-stagger="2">
          {lead}
        </h1>
        <div className={cfg.bodyClass} data-stagger="3">
          {typeof body === 'string' ? <p>{body}</p> : body}
        </div>
        {children ? <div className="brisa-placeholder-extra">{children}</div> : null}
        <div className="brisa-placeholder-squiggle-wrap" data-stagger="4">
          <Squiggle
            rotation={cfg.squiggle.rotation}
            scale={cfg.squiggle.scale}
            skew={cfg.squiggle.skew}
          />
        </div>
      </div>
      <footer className="brisa-placeholder-footer">
        {footer ?? <p>brisapass.com · © 2026</p>}
      </footer>
    </main>
  );
}

PlaceholderState.displayName = 'PlaceholderState';
